/**
 * ACT2-1108: the five steps of the first-run "Set up Lookout" panel (`SETUP_STEPS`) and which are done.
 * Pure, so the order, the count and the "current step" rule are testable without rendering.
 *
 * The server decides the four COUNTED steps (`setup.status`): the warehouse, and repos, context and invite,
 * each done when actually done or skipped for the workspace by an admin. "Tweak your workspace settings" is
 * decided here: done once this person said the settings look good. Once setup is complete (recorded once on
 * the server, and final), every step reads done, so a complete workspace always reads 5 of 5 even after a
 * repo is unlinked.
 */
import type { RouterClient } from '@orpc/server';
import type { AppRouter } from '@server/rpc';

export type WorkspaceSetupStatus = Awaited<ReturnType<RouterClient<AppRouter>['setup']['status']>>['status'];

export type SetupStepTag = 'Required' | 'Recommended' | 'Optional';

export interface SetupStepConfig {
  id: 'warehouse' | 'repos' | 'context' | 'settings' | 'invite';
  title: string;
  /** What the step is for: its subtitle until it is done. */
  description: string;
  tag: SetupStepTag;
  /**
   * The step's own control moves the panel on ("Looks good" on the settings step), so the panel adds no
   * Continue to it.
   */
  ownContinue?: boolean;
}

/**
 * The panel's steps, in panel order. The only list: the order, the numbers on screen, the "of N" total, the
 * first open step and Continue's target all come from it.
 */
export const SETUP_STEPS = [
  {
    id: 'warehouse',
    title: 'Connect your warehouse',
    tag: 'Required',
    description: 'Lookout can’t answer data questions until this is done.',
  },
  {
    id: 'repos',
    title: 'Link your relevant repos',
    tag: 'Recommended',
    description: 'Table meanings and metric definitions, read from GitHub.',
  },
  {
    id: 'context',
    title: 'Add business context',
    tag: 'Optional',
    description: 'A Context Hub note on your business and key terms.',
  },
  {
    id: 'settings',
    title: 'Tweak your workspace settings',
    tag: 'Optional',
    description: 'AI controls and public dashboards.',
    ownContinue: true,
  },
  {
    id: 'invite',
    title: 'Invite a team member',
    tag: 'Optional',
    description: 'Bring in the people who’ll build audiences.',
  },
] as const satisfies readonly SetupStepConfig[];

export type SetupStepId = (typeof SETUP_STEPS)[number]['id'];
export const SETUP_STEP_IDS: readonly SetupStepId[] = SETUP_STEPS.map((step) => step.id);
export const SETUP_STEP_TOTAL = SETUP_STEPS.length;

export function setupStepsDone(
  status: WorkspaceSetupStatus,
  { settingsReviewed }: { settingsReviewed: boolean },
): Record<SetupStepId, boolean> {
  const complete = status.complete;
  return {
    warehouse: complete || status.warehouseConnected,
    repos: complete || status.reposLinked || status.reposSkipped,
    context: complete || status.contextAdded || status.contextSkipped,
    // Optional and never counted: once setup is complete, so is this step.
    settings: complete || settingsReviewed,
    invite: complete || status.inviteSent || status.inviteSkipped,
  };
}

export function setupDoneCount(done: Record<SetupStepId, boolean>): number {
  return SETUP_STEP_IDS.filter((id) => done[id]).length;
}

/** The first step still to do, in panel order, or null when all are done. */
export function firstOpenSetupStep(done: Record<SetupStepId, boolean>): SetupStepId | null {
  return SETUP_STEP_IDS.find((id) => !done[id]) ?? null;
}

/** The steps an admin can skip for the whole workspace (`setup.skipStep`). */
export type SkippableSetupStepId = Extract<SetupStepId, 'repos' | 'context' | 'invite'>;
